import { useSnackbar } from 'notistack'
import React, { useEffect, useState } from 'react'
import ReactSelect from 'react-select'

import ApiService from '@/services/Api'
import {
    Add as AddIcon,
    EditOutlined as EditIcon,
    DeleteOutline as DeleteIcon,
    ContentCopyOutlined as CopyIcon
} from '@mui/icons-material'
import {
    Grid,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Paper,
    TextField,
    Button,
    TablePagination,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogTitle,
    Divider,
    FormControlLabel,
    Switch,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    ListItemText,
    SelectChangeEvent,
    Typography
} from '@mui/material'

import useStyles from './style'
import ApiKeyExpirationTooltip from '@/components/ExpirationTooltip'
import statesAndCities from '@/assets/statesAndCities.json'

interface IKey {
    uuid?: string;
    title: string;
    description?: string;
    expiresInterval: number;
    generateNewKeyWhenExpires: boolean;
    apiToken: string;
    permissions: string[];
    allowedCompanies: string[];
    usageCount?: number;
}

interface ICompany {
    uuid: string;
    name: string;
    frequency?: number | string | null;
    address?: { city?: string | null; state?: string | null } | null;
}

function getCompanyLabel(company: ICompany): string {
    let label = company.name

    if (company.frequency !== null && company.frequency !== undefined && company.frequency !== '') {
        label += ` (${company.frequency})`
    }

    let uf = company.address?.state
    if (company.address?.state) {
        const state = statesAndCities.find(state => state.name === company.address?.state)
        if (state) uf = state.initials
    }

    const city = company.address?.city

    if (city && uf) {
        label += ` - ${city}/${uf}`
    } else if (city) {
        label += ` - ${city}`
    } else if (uf) {
        label += ` - ${uf}`
    }

    return label
}

const options = [
    { value: '/distribute/banner/:streamId', label: '/distribute/banner/:id' },
    { value: '/sendMessage', label: '/sendMessage' },
    { value: '/sendDocument', label: '/sendDocument' },
    { value: '/midroll/distributions/:streamId', label: '/midroll/distributions/:streamId' },
    { value: '/midroll/distribute/:streamId', label: '/midroll/distribute/:streamId' },
]

const Keys: React.FC = () => {
    const classes = useStyles()
    const { enqueueSnackbar } = useSnackbar()
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(6)
    const [keys, setKeys] = useState<IKey[]>([])
    const [companies, setCompanies] = useState<ICompany[]>([])
    const [key, setKey] = useState<IKey | null>(null)
    const [loadingKey, setLoadingKey] = useState(false)
    const [searchTerm, setSearchTerm] = useState<string>('')
    const [openedKeyDialog, setOpenedKeyDialog] = useState(false)
    const [loadingDeleteKey, setLoadingDeleteKey] = useState(false)
    const [openedDeleteKeyDialog, setOpenedDeleteKeyDialog] = useState(false)
    const [showValidationErrors, setShowValidationErrors] = useState(false)

    const fetchKeys = async () => {
        try {
            const response = await ApiService.get('/key/list')
            return response.data
        } catch (error) {
            console.error('Erro ao buscar chaves: ', error)
            return []
        }
    }

    const fetchRadios = async () => {
        try {
            const response = await ApiService.get('/company/list')
            return response.data
        } catch (error) {
            console.error('Erro ao buscar rádios: ', error)
            return []
        }
    }

    useEffect(() => {
        const loadData = async () => {
            const [fetchedKeys, fetchedCompanies] = await Promise.all([
                fetchKeys(),
                fetchRadios()
            ])
            setKeys(fetchedKeys)
            setCompanies(fetchedCompanies)
        }
        loadData()
    }, [])

    const handleOpenKeyDialog = (keyToEdit?: IKey) => {
        const initialKeyData = keyToEdit ? {
            ...keyToEdit,
            // Garantir que sempre sejam arrays, mesmo se vier como null/undefined do backend
            permissions: Array.isArray(keyToEdit.permissions) ? keyToEdit.permissions : [],
            allowedCompanies: Array.isArray(keyToEdit.allowedCompanies) ? keyToEdit.allowedCompanies : []
        } : {
            title: '',
            description: '',
            expiresInterval: 10,
            generateNewKeyWhenExpires: false,
            apiToken: '',
            permissions: [], // Array vazio garantido
            allowedCompanies: [] // Array vazio garantido
        }

        setKey(initialKeyData)
        setShowValidationErrors(false) // Resetar os erros de validação
        setOpenedKeyDialog(true)
    }

    const saveKey = async (): Promise<void> => {
        if (!key) {
            enqueueSnackbar('Nenhum dado fornecido.', { variant: 'error' })
            return
        }

        // Ativar a exibição de erros de validação
        setShowValidationErrors(true)

        const requiredFields: { key: keyof typeof key, label: string }[] = [
            { key: 'title', label: 'título' },
            { key: 'expiresInterval', label: 'intervalo de expiração' }
        ]

        for (const { key: fieldKey, label } of requiredFields) {
            if (!key[fieldKey]) {
                enqueueSnackbar(`O ${label} é obrigatório e não pode estar vazio.`, { variant: 'error' })
                return;
            }
        }

        // Validação específica para permissões
        if (!key.permissions || key.permissions.length === 0) {
            enqueueSnackbar('É obrigatório selecionar pelo menos uma permissão.', { variant: 'error' })
            return;
        }

        setLoadingKey(true)

        try {
            // GARANTIR QUE ARRAYS SEJAM VÁLIDOS ANTES DO ENVIO
            const dataToSend = {
                ...key,
                // Garantir que permissions seja sempre um array válido
                permissions: Array.isArray(key.permissions) ? key.permissions : [],
                // Garantir que allowedCompanies seja sempre um array válido
                allowedCompanies: Array.isArray(key.allowedCompanies) ? key.allowedCompanies : []
            }


            const endpoint = key.uuid ? `/key/${key.uuid}` : "/key"
            const method = key.uuid ? ApiService.put : ApiService.post
            const response = await method(endpoint, dataToSend) // Usa dataToSend ao invés de key

            setKeys(prevKeys =>
                key.uuid
                    ? prevKeys.map(k => k.uuid === key.uuid ? response.data : k)
                    : [response.data, ...prevKeys]
            )
            enqueueSnackbar('Chave de API salva com sucesso!', { variant: 'success' })
            handleCloseKeyDialog()
        } catch (e) {
            console.error('Erro ao salvar a Chave:', e)
            enqueueSnackbar('Ocorreu um erro ao salvar a chave.', { variant: 'error' })
        } finally {
            setLoadingKey(false)
        }
    }

    const handleDeleteKey = async () => {
        setLoadingDeleteKey(true)
        try {
            await ApiService.delete(
                `/key/${key?.uuid}`
            )
            setKeys(prevKeys => prevKeys.filter(k => k.uuid !== key?.uuid))
            enqueueSnackbar('Chave de API removida com sucesso.', {
                variant: 'success'
            })
            setOpenedDeleteKeyDialog(false)
        } catch (error) {
            enqueueSnackbar(
                'Ocorreu um erro ao remover a chave.',
                {
                    variant: 'error'
                }
            )
        } finally {
            setLoadingDeleteKey(false)
        }
    }

    const handleCopy = async (apiToken: string) => {
        try {
            await navigator.clipboard.writeText(apiToken)
            enqueueSnackbar('Chave de API copiada com sucesso', {
                variant: 'success'
            })
        } catch (err) {
            enqueueSnackbar('Ocorreu um erro ao copiar a chave', {
                variant: 'error'
            })
        }
    }

    const handleChangePage = (_: unknown, newPage: React.SetStateAction<number>) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (event: { target: { value: string; }; }) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    const openDeleteKeyDialog = async (key: IKey) => {
        setKey(key)
        setOpenedDeleteKeyDialog(true)
    }

    const handleCloseKeyDialog = () => {
        setOpenedKeyDialog(false)
        setShowValidationErrors(false) // Resetar os erros ao fechar
        setKey(null)
    }

    const handlePermissionsChange = (e: SelectChangeEvent<string[]>) => {
        if (!key) return
        const permissions = e.target.value

        // Garantir que sempre seja um array
        const validPermissions = Array.isArray(permissions) ? permissions : []

        setKey({
            ...key,
            permissions: validPermissions
        })
    }

    const getSelectedCompanies = () => {
        if (!key?.allowedCompanies || !Array.isArray(key.allowedCompanies)) return []

        return companies
            .filter(company => key.allowedCompanies.includes(company.uuid))
            .map(company => ({ value: company.uuid, label: getCompanyLabel(company) }))
    }

    const handleCompaniesChange = (selectedOptions: any) => {
        if (!key) return

        // Garantir que sempre seja um array
        const allowedCompanies = selectedOptions && Array.isArray(selectedOptions)
            ? selectedOptions.map((option: any) => option.value)
            : []

        setKey({
            ...key,
            allowedCompanies
        })
    }

    const companyOptions = companies.map(company => ({
        value: company.uuid,
        label: getCompanyLabel(company)
    }))

    const filteredKeys = keys.filter((key) => {
        const lowerCaseSearchTerm = searchTerm.toLowerCase()
        const matchesName = key.title.toLowerCase().includes(lowerCaseSearchTerm)
        return matchesName
    })

    const apiKeysToDisplay = filteredKeys.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)

    return (
        <>
            <Grid container spacing={4}>
                <Grid item xs={6}>
                    <TextField
                        id="key_search_field"
                        fullWidth
                        label="Buscar por título"
                        variant="outlined"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        size="small"
                    />
                </Grid>
                <Grid
                    item
                    xs={6}
                    style={{ display: 'flex', justifyContent: 'flex-end' }}
                >
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenKeyDialog()}
                        size="small"
                    >
                        Criar Chave
                    </Button>
                </Grid>
                <Grid item xs={12}>
                    <TableContainer component={Paper}>
                        <Table aria-label="keys table" >
                            <TableHead>
                                <TableRow sx={{ height: '30px' }}>
                                    <TableCell>Título</TableCell>
                                    <TableCell>Descrição</TableCell>
                                    <TableCell>Token</TableCell>
                                    <TableCell align="center">Expiração</TableCell>
                                    <TableCell align="center">Gerar nova chave?</TableCell>
                                    <TableCell align="center">Permissões</TableCell>
                                    <TableCell align="center">Usos</TableCell>
                                    <TableCell align="right">Ações</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {apiKeysToDisplay.map((apiKey) => (
                                    <TableRow key={apiKey.uuid} sx={{ height: '30px' }}>
                                        <TableCell component="th" scope="row">{apiKey.title}</TableCell>
                                        <TableCell className={classes.truncatedText} title={apiKey.description}>{apiKey.description}</TableCell>
                                        <TableCell className={classes.truncatedText}>{apiKey.apiToken}</TableCell>
                                        <TableCell align="center">{apiKey.expiresInterval}m</TableCell>
                                        <TableCell align="center">{apiKey.generateNewKeyWhenExpires ? 'Sim' : 'Não'}</TableCell>
                                        <TableCell align="center">
                                            {apiKey.permissions && Array.isArray(apiKey.permissions) && apiKey.permissions.length > 0
                                                ? apiKey.permissions.join(', ')
                                                : 'Nenhuma'
                                            }
                                        </TableCell>
                                        <TableCell align="center">{apiKey.usageCount || 0}</TableCell>
                                        <TableCell align="right">
                                            <IconButton onClick={() => handleCopy(apiKey.apiToken)}>
                                                <CopyIcon />
                                            </IconButton>
                                            <IconButton onClick={() => handleOpenKeyDialog(apiKey)}>
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton onClick={() => openDeleteKeyDialog(apiKey)}>
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        <TablePagination
                            rowsPerPageOptions={[6, 7, 8, 9, 10]}
                            component="div"
                            count={filteredKeys.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                    </TableContainer>
                </Grid>
            </Grid >

            <Dialog open={openedKeyDialog}>
                <DialogTitle>
                    {
                        !key?.uuid
                            ? 'Criando nova chave'
                            : 'Editando a chave'
                    }
                </DialogTitle>

                {key && (
                    <>
                        <Grid container spacing={2} style={{ padding: '16px' }}>
                            <Grid item xs={12} sm={12}>
                                <TextField
                                    id="key_title_field"
                                    fullWidth
                                    label="Título"
                                    size="small"
                                    variant="outlined"
                                    value={key.title}
                                    onChange={(e) =>
                                        setKey({ ...key, title: e.target.value })
                                    }
                                />
                            </Grid>
                            <Grid item xs={12} sm={12}>
                                <TextField
                                    id="key_description_field"
                                    type="textarea"
                                    label="Descrição"
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    value={key.description}
                                    onChange={({ target }) => setKey({ ...key, description: target.value })}
                                    multiline
                                    rows={4}
                                    inputProps={{ maxLength: 255 }}
                                    style={{ width: '100%' }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={12}>
                                <TextField
                                    id="key_token_field"
                                    type="textarea"
                                    label="Token de acesso"
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    value={key.apiToken}
                                    onChange={({ target }) => setKey({ ...key, apiToken: target.value })}
                                    multiline
                                    rows={4}
                                    inputProps={{ maxLength: 255 }}
                                    style={{ width: '100%' }}
                                    disabled={!!key.uuid} // Desabilita quando está editando (tem uuid)
                                />
                                {!key.uuid && (
                                    <Typography
                                        variant="caption"
                                        color="textSecondary"
                                        style={{ marginTop: '8px', display: 'block' }}
                                    >
                                        ℹ️ Se nenhum token for inserido, ele será gerado automaticamente.
                                    </Typography>
                                )}
                            </Grid>

                            <Grid item xs={12} sm={12}>
                                <div style={{ position: 'relative' }}>
                                    <label
                                        style={{
                                            position: 'absolute',
                                            backgroundColor: 'white',
                                            padding: '0 4px',
                                            top: '-10px',
                                            left: '10px',
                                            fontSize: '12px',
                                            zIndex: 1,
                                            color: 'rgba(0, 0, 0, 0.6)',
                                            fontFamily: 'Roboto, sans-serif'
                                        }}
                                    >
                                        Rádio
                                    </label>
                                    <ReactSelect
                                        inputId="radio_select_field"
                                        isMulti
                                        value={getSelectedCompanies()}
                                        onChange={handleCompaniesChange}
                                        options={companyOptions}
                                        placeholder="Selecione as rádios..."
                                        classNamePrefix="react-select"
                                        isSearchable
                                        hideSelectedOptions={false}
                                        isClearable={false}
                                        closeMenuOnSelect={false}
                                        styles={{
                                            control: (baseStyles) => ({
                                                ...baseStyles,
                                                minHeight: '40px',
                                                borderRadius: '4px',
                                                border: '1px solid rgba(0, 0, 0, 0.23)',
                                                boxShadow: 'none',
                                                fontFamily: 'Roboto, sans-serif',
                                                '&:hover': {
                                                    border: '1px solid rgba(0, 0, 0, 0.87)'
                                                }
                                            }),
                                            menu: (baseStyles) => ({
                                                ...baseStyles,
                                                zIndex: 9999,
                                                fontFamily: 'Roboto, sans-serif'
                                            }),
                                            multiValue: (baseStyles) => ({
                                                ...baseStyles,
                                                margin: '2px',
                                                fontFamily: 'Roboto, sans-serif',
                                                flexShrink: 0
                                            }),
                                            valueContainer: (baseStyles) => ({
                                                ...baseStyles,
                                                padding: '2px 8px',
                                                overflow: 'auto',
                                                whiteSpace: 'nowrap',
                                                flexWrap: 'nowrap',
                                                fontFamily: 'Roboto, sans-serif',
                                                maxWidth: 'none',
                                                width: 'auto',
                                                flexGrow: 1,
                                                display: 'flex',
                                                '::-webkit-scrollbar': {
                                                    height: '4px'
                                                },
                                                '::-webkit-scrollbar-thumb': {
                                                    background: 'rgba(0,0,0,0.2)',
                                                    borderRadius: '2px'
                                                }
                                            }),
                                            container: (baseStyles) => ({
                                                ...baseStyles,
                                                width: '100%',
                                            }),
                                            option: (baseStyles, { isSelected }) => ({
                                                ...baseStyles,
                                                fontFamily: 'Roboto, sans-serif',
                                                backgroundColor: isSelected ? 'rgba(25, 118, 210, 0.08)' : baseStyles.backgroundColor,
                                                color: isSelected ? '#1976d2' : baseStyles.color,
                                                '&:active': {
                                                    backgroundColor: isSelected ? 'rgba(11, 83, 156, 0.56)' : 'rgba(228, 230, 233, 0.16)'
                                                }
                                            }),
                                            input: (baseStyles) => ({
                                                ...baseStyles,
                                                fontFamily: 'Roboto, sans-serif',
                                                flexShrink: 0
                                            }),
                                            placeholder: (baseStyles) => ({
                                                ...baseStyles,
                                                fontFamily: 'Roboto, sans-serif'
                                            }),
                                            singleValue: (baseStyles) => ({
                                                ...baseStyles,
                                                fontFamily: 'Roboto, sans-serif'
                                            })
                                        }}
                                        components={{
                                            MultiValueContainer: ({ children, ...props }) => (
                                                <div style={{ display: 'flex', flexShrink: 0 }} {...props}>
                                                    {children}
                                                </div>
                                            )
                                        }}
                                    />
                                </div>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    id="key_expiration_field"
                                    fullWidth
                                    label="Intervalo de expiração (minutos)"
                                    size="small"
                                    variant="outlined"
                                    type="number"
                                    value={key.expiresInterval}
                                    onChange={(e) =>
                                        setKey({ ...key, expiresInterval: +e.target.value })
                                    }
                                />
                                <ApiKeyExpirationTooltip minutes={key.expiresInterval} />
                            </Grid>
                            <Grid item xs={12} sm={6} className={classes.dialogSelectItemInput}>
                                <FormControl variant="outlined" className={classes.formControl} size='small'>
                                    <InputLabel id="key_permissions_label">Permissões *</InputLabel>
                                    <Select
                                        id="key_permissions_field"
                                        labelId="key_permissions_label"
                                        value={Array.isArray(key.permissions) ? key.permissions : []}
                                        onChange={handlePermissionsChange}
                                        variant="outlined"
                                        color="primary"
                                        multiple
                                        renderValue={(selected: string[]) =>
                                            Array.isArray(selected) ? selected.join(', ') : ''
                                        }
                                        label="Permissões *"
                                        error={showValidationErrors && (!key.permissions || key.permissions.length === 0)}
                                        MenuProps={{
                                            PaperProps: {
                                                style: {
                                                    maxHeight: 224,
                                                    width: 250
                                                }
                                            }
                                        }}
                                    >
                                        {
                                            options.map((option) => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    <Checkbox
                                                        id={`permission_${option.value.replace(/[^a-zA-Z0-9]/g, '_')}_checkbox`}
                                                        color='primary'
                                                        checked={Array.isArray(key.permissions) && key.permissions.includes(option.value)}
                                                    />
                                                    <ListItemText primary={option.label} />
                                                </MenuItem>
                                            ))
                                        }
                                    </Select>
                                    {(showValidationErrors && (!key.permissions || key.permissions.length === 0)) && (
                                        <Typography
                                            variant="caption"
                                            color="error"
                                            style={{ marginTop: '8px', marginLeft: '14px' }}
                                        >
                                            Selecione pelo menos uma permissão
                                        </Typography>
                                    )}
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={12} className={classes.dialogItemInput}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            id="key_generate_new_field"
                                            checked={key.generateNewKeyWhenExpires}
                                            onChange={({ target }) => setKey({ ...key, generateNewKeyWhenExpires: target.checked })}
                                            color="primary"
                                        />
                                    }
                                    label={'Gerar uma nova chave quando a atual expirar'}
                                />
                            </Grid>
                        </Grid>

                        <Divider />

                        <DialogActions>
                            <Button
                                onClick={handleCloseKeyDialog}
                                color="primary"
                            >
                                Cancelar
                            </Button>
                            <Button
                                onClick={saveKey}
                                color="primary"
                                variant="contained"
                                disabled={loadingKey}
                                endIcon={loadingKey && (
                                    <CircularProgress size={20} color="inherit" />
                                )}
                            >
                                {key.uuid ? 'Salvar' : 'Criar'}
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>
            <Dialog
                aria-labelledby="simple-dialog-title"
                open={openedDeleteKeyDialog}
            >
                <DialogTitle>
                    Tem certeza que deseja remover a chave {key?.title}
                </DialogTitle>
                <DialogActions>
                    <Button
                        onClick={() => setOpenedDeleteKeyDialog(false)}
                        className={classes.deleteKeyDialogCancelButton}
                        disabled={loadingDeleteKey}
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleDeleteKey}
                        color="primary"
                        autoFocus
                        disabled={loadingDeleteKey}
                        endIcon={
                            loadingDeleteKey && (
                                <CircularProgress size={15} color="inherit" />
                            )
                        }
                    >
                        Remover
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default Keys
